"use client";

import { X } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useStickyFooterClaimed } from "@/lib/monetization/sticky-footer-claim";

const STORAGE_KEY = "job_view_count";
const DISMISSED_KEY = "feed_nudge_dismissed";
const THRESHOLD = 2;

type Props = {
	locale: string;
};

export function FeedDiscoveryNudge({ locale }: Props) {
	const [visible, setVisible] = useState(false);
	// A page-owned conversion CTA (the mobile Apply bar) owns the bottom slot;
	// this nudge is ambient and must never paint over it — the ad bar did
	// this once before, and the feedback widget was still doing it on mobile
	// job pages as of 2026-07.
	const stickyFooterClaimed = useStickyFooterClaimed();

	useEffect(() => {
		try {
			if (localStorage.getItem(DISMISSED_KEY)) return;
			const raw = localStorage.getItem(STORAGE_KEY);
			const count = raw ? Number.parseInt(raw, 10) : 0;
			const next = count + 1;
			localStorage.setItem(STORAGE_KEY, String(next));
			if (next >= THRESHOLD) setVisible(true);
		} catch {
			// localStorage unavailable — skip silently
		}
	}, []);

	function dismiss() {
		try {
			localStorage.setItem(DISMISSED_KEY, "1");
		} catch {
			// ignore
		}
		setVisible(false);
	}

	if (!visible || stickyFooterClaimed) return null;

	const isEn = locale === "en";

	return (
		// Offset by the bottom ad bar's height (--ad-bar-h, 0px when none) so the
		// z-40 bar never covers this z-30 nudge.
		<div className="fixed bottom-[calc(var(--ad-bar-h,0px)+1rem)] left-1/2 z-30 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 rounded-xl border border-border bg-card px-4 py-3 shadow-lg sm:bottom-[calc(var(--ad-bar-h,0px)+1.5rem)]">
			<div className="flex items-center justify-between gap-3">
				<p className="text-sm text-foreground">
					{isEn ? (
						<>
							See more jobs matched to your profile.{" "}
							<Link
								href={`/${locale}/jobs`}
								onClick={dismiss}
								className="font-semibold text-primary underline underline-offset-2"
							>
								Open your feed →
							</Link>
						</>
					) : (
						<>
							Temukan lebih banyak lowongan sesuai profilmu.{" "}
							<Link
								href={`/${locale}/jobs`}
								onClick={dismiss}
								className="font-semibold text-primary underline underline-offset-2"
							>
								Buka feed-mu →
							</Link>
						</>
					)}
				</p>
				<button
					type="button"
					onClick={dismiss}
					aria-label={isEn ? "Dismiss" : "Tutup"}
					className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
				>
					<X className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}
